import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

import { Form, message } from "antd";
import { useLocation } from "react-router-dom";
import debounce from "lodash.debounce";

import { useAppDispatch, useAppSelector } from "store";
import { getUserSelector } from "store/user/user.selector";
import { ICaseEquipment, Severity } from "store/cases/cases.types";
import { useContractors } from "./useContractors";
import { isValidEmail } from "./fieldValidation";
import api from "services/api.service";
import { ICaseForOpen } from "services/interface.service";
import { RouteLinks } from "services/router.service";
import { getCases } from "store/cases/cases.thunks";
import { formatPhoneNumber } from "services/data.service";
import { setCaseModal } from "store/cases/cases.actions";

interface IUseCaseForm {
  serial_number: string;
  afterEvent: () => void;
  id?: number;
  fileAttachments?: number[];
  filesUploadStatus: string[];
  setFiles: Dispatch<SetStateAction<File[]>>;
  setFileAttachments: Dispatch<SetStateAction<number[]>>;
  setFilesUploadStatus: Dispatch<SetStateAction<string[]>>;
}

export const useCaseForm = ({
  serial_number,
  afterEvent,
  id,
  fileAttachments,
  filesUploadStatus,
  setFiles,
  setFileAttachments,
  setFilesUploadStatus,
}: IUseCaseForm) => {
  const [ form ] = Form.useForm();

  const subject = Form.useWatch("subject", form);
  const serialNumber = Form.useWatch("serial_number", form);
  const authorName = Form.useWatch("author_name", form);
  const authorEmail = Form.useWatch("author_email", form);
  const authorPhoneNumber = Form.useWatch("author_phone_number", form);
  const customerValue = Form.useWatch("customerValue", form);
  const subcontractorValue = Form.useWatch("subcontractor", form);

  const location = useLocation();
  const dispatch = useAppDispatch();

  const {
    isAuthorized,
    isMobile,
    user,
  } = useAppSelector(getUserSelector);

  const [ caseEquipment, setCaseEquipment ] = useState<ICaseEquipment | null>(null);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isCustomerEdit, setCustomerIsEdit ] = useState(true);
  const [ isSubcontractorEdit, setSubcontractorIsEdit ] = useState(true);
  const [ clients, setClients ] = useState<any[]>([]);
  const [ clientsCopy, setClientsCopy ] = useState<any[]>([]);
  const [ author, setAuthor ] = useState<any>(null);
  const [ contractor, setContractor ] = useState<any>(null);
  const [ subcontractor, setSubcontractor ] = useState<any>(null);
  const [ serialNumberError, setSerialNumberError ] = useState("");
  const [ isCleared, setIsCleared ] = useState(false);
  const [ isAuthorEmailExist, setIsAuthorEmailExist ] = useState(false);

  const { contractors, getContractors } = useContractors(100);

  const isSubmitDisabled =
    !subject ||
    !customerValue ||
    !subcontractorValue ||
    !authorName ||
    !authorEmail ||
    filesUploadStatus.length > 0;

  // const { getRootProps, getInputProps, isDragActive } = useDropzone({
  // 	onDrop: useCallback((acceptedFiles: File[]) => {
  // 		let result: File[] = [];
  // 		acceptedFiles && Object.keys(acceptedFiles).forEach((label) => {
  // 			result.push(acceptedFiles![ +label ])
  // 		})
  // 		setFiles([ ...files, ...result ])
  // 	}, []),
  // 	multiple: true,
  // })

  const getClients = async (search: string) => {
    if (contractor) {
      try {
        const response = await api.contacts(
          contractor.value || contractor.id,
          // @ts-ignore
          { search },
          { page: 1, rowsPerPage: 20, count: 0 },
        );

        setClients([ ...response.data.map((res) => ({
          id: res.id,
          value: `${ res.first_name } ${ res.last_name }`,
          label: res.first_name,
          lastName: res.last_name,
          email: res.email,
          phone_number: res.phone_number,
        })) ]);
      } catch (e: any) {
        message.error(e.response.data.error || e.error || "Something went wrong!");
      }
    }
  }

  const debouncedClients = useMemo(() => {
    return debounce(getClients, 400);
  }, [ contractor ])

  const getClientsCopy = async (search: string) => {
    if (contractor) {
      try {
        const response = await api.contacts(
          contractor.value || contractor.id,
          // @ts-ignore
          { search },
          { page: 1, rowsPerPage: 20, count: 0 },
        );

        setClientsCopy([ ...response.data.map((res) => ({
          value: res.first_name,
          label: res.first_name,
          lastName: res.last_name,
          email: res.email,
          phone_number: res.phone_number,
        })) ]);
      } catch (e: any) {
        message.error(e.response.data.error || e.error || "Something went wrong!");
      }
    }
  };

  const handleContactCreate = async () => {
    try {
      await api.contactCreate(contractor.value || contractor.id, {
        email: authorEmail,
        first_name: authorName,
      });

      setIsAuthorEmailExist(true);

      await getClients(authorName);

      message.success("Contact successfully added.");
    } catch (e: any) {
      message.error(e.response.data.error || e.error || "Something went wrong!");
    }
  };

  const handleCompleteForm = async (values: ICaseForOpen) => {
    try {
      setIsLoading(true);

      const filteredEmailCC = values.email_cc ? values.email_cc.filter((email: string) => {
        const isExistingOption = clientsCopy.some(client => client.email === email);
        return isExistingOption || isValidEmail(email);
      }) : [];

      await api.openCase({
          ...values,
          email_cc: filteredEmailCC,
          attachments: fileAttachments,
          serial_number: !caseEquipment ? values.serial_number : undefined,
        },
        contractor ? contractor.id || contractor.value : undefined,
        subcontractor ? subcontractor.id || subcontractor.value : undefined,
        caseEquipment ? caseEquipment.id : undefined,
      );

      setIsLoading(false);
      message.success("Case was created.")
      afterCompleteEvent();
    } catch (e: any) {
      setIsLoading(false);
      message.error(e.response.data.error || e.error || "Something went wrong!");
    }
  }

  const afterCompleteEvent = () => {
    form.setFieldValue("subject", "");
    form.setFieldValue("serial_number", "");
    form.setFieldValue("text", "");
    form.setFieldValue("subcontractor", null);
    form.setFieldValue("email_cc", null);
    form.setFieldValue("severity", Severity.NORMAL);

    setSerialNumberError("");
    setFiles([]);

    location.pathname === RouteLinks.PROTECTED.CASES.MAIN && dispatch(getCases());
    afterEvent?.();
  }

  const findEquipment = async (serial_number: string, id?: number) => {
    if (serial_number !== "" && isAuthorized && user) {
      try {
        const { data: equipment } = await api.getEquipmentForCase(serial_number, id);

        setCaseEquipment(equipment);

        if (equipment) {
          setSubcontractorIsEdit(false);

          if (equipment.customer.is_my_org) {
            setCustomerIsEdit(false);
          }

          form.setFieldValue("customerValue", equipment.customer.name);
          form.setFieldValue("subcontractor", equipment.subcontractor.name);

          setContractor(equipment.customer);
          setSubcontractor(equipment.subcontractor);

          const data = equipment.customer.is_my_org ? {
            name: `${ user.first_name } ${ user.last_name }`,
            email: user.email,
            phone_number: user.phone_number,
          } : {
            name: "",
            email: "",
            phone_number: "",
          };

          form.setFieldValue("author_name", data.name);
          form.setFieldValue("author_email", data.email);
          form.setFieldValue(
            "author_phone_number",
            data.phone_number ? formatPhoneNumber(data.phone_number) : "",
          );
        }

        serialNumberError && setSerialNumberError("");
      } catch (e: any) {
        setCaseEquipment(null);
        setSerialNumberError(e.response.data.detail);

        // setContractor(null);
        // setSubcontractor(null);
        // setSubcontractorIsEdit(true);
        // form.setFieldValue("subcontractor", null);
      }
    } else {
      setCaseEquipment(null);
      setSerialNumberError("");
    }
  }

  const onCustomerSave = () => {
    setCustomerIsEdit(false);
  };

  const onSubcontractorSave = () => {
    setSubcontractorIsEdit(false);
  };

  const onCustomerChange = async (value: any) => {
    const { data: customer } = await api.getContractorRelatedMyOrg(value);

    setSubcontractor(customer);
    form.setFieldValue("subcontractor", customer.name);

    const contractorData = contractors.find((c) => c.value === value);

    setContractor(contractorData);

    const data = contractorData?.is_my_org && user ? {
      name: `${ user.first_name } ${ user.last_name }`,
      email: user.email,
      phone_number: user.phone_number,
    } : {
      name: "",
      email: "",
      phone_number: "",
    };

    setAuthor(data.name);
    form.setFieldValue("author_name", data.name);
    form.setFieldValue("author_email", data.email);
    form.setFieldValue(
      "author_phone_number",
      data.phone_number ? formatPhoneNumber(data.phone_number) : "",
    );

    setSubcontractorIsEdit(false);
    setSerialNumberError("");
  };

  const onSubcontractorChange = async (value: any) => {
    setSubcontractor(contractors.find(c => c.value === value));

    const { data: customer } = await api.getContractorRelatedMyOrg(value);

    const data = customer.is_my_org && user ? {
      name: `${ user.first_name } ${ user.last_name }`,
      email: user.email,
      phone_number: user.phone_number,
    } : {
      name: "",
      email: "",
      phone_number: "",
    };

    setAuthor(data.name);
    form.setFieldValue("author_name", data.name);
    form.setFieldValue("author_email", data.email);
    form.setFieldValue(
      "author_phone_number",
      data.phone_number ? formatPhoneNumber(data.phone_number) : "",
    );

    setContractor(customer);
    form.setFieldValue("customerValue", customer.name);

    setCustomerIsEdit(false);
  };

  const onSubcontractorEdit = () => {
    setSubcontractorIsEdit(true);
    // setSubcontractor(null);
  };

  const onContactEmailCheck = async (id: number, email: string) => {
    try {
      await api.getContractorContactsListCheck(id, email);

      setIsAuthorEmailExist(true);
    } catch (error) {
      setIsAuthorEmailExist(false);
    }
  };

  const debouncedContactEmailCheck = useMemo(() => {
    return debounce(onContactEmailCheck, 400);
  }, []);

  const onResetForm = () => {
    form.resetFields();
    setIsCleared(true);

    dispatch(setCaseModal({
      isOpen: true,
    }));

    setContractor(null);
    setSubcontractor(null);
    setFiles([]);
    setCaseEquipment(null);
    setCustomerIsEdit(true);
    setSubcontractorIsEdit(true);
    setIsLoading(false);
    setClients([]);
    setClientsCopy([]);
    setAuthor(null);
    setSerialNumberError("");
    setFileAttachments([]);
    setFilesUploadStatus([]);
  };

  useEffect(() => {
    getClients("");
  }, [ contractor ]);

  useEffect(() => {
    getContractors("");
    getClientsCopy("");
    // getClients("");

    serial_number !== "" && findEquipment(serial_number, id);
    form.setFieldValue("serial_number", serial_number);

    // if (isAuthorized && user) {
    // 	setAuthor(user.first_name + " " + user.last_name);
    //
    // 	form.setFieldValue("author_name", user.first_name + " " + user.last_name);
    // 	form.setFieldValue("author_email", user.email);
    // 	form.setFieldValue("author_phone_number", formatPhoneNumber(user.phone_number));
    // }
  }, []);

  return {
    isMobile,
    form,
    handleCompleteForm,
    isCleared,
    onResetForm,
    findEquipment,
    caseEquipment,
    serialNumberError,
    isCustomerEdit,
    onCustomerSave,
    contractor,
    authorEmail,
    contractors,
    getContractors,
    onCustomerChange,
    debouncedClients,
    clients,
    handleContactCreate,
    onSubcontractorSave,
    subcontractor,
    isSubcontractorEdit,
    serialNumber,
    onSubcontractorChange,
    clientsCopy,
    setCustomerIsEdit,
    authorName,
    authorPhoneNumber,
    author,
    setAuthor,
    onSubcontractorEdit,
    getClientsCopy,
    isSubmitDisabled,
    isLoading,
    onContactEmailCheck: debouncedContactEmailCheck,
    isAuthorEmailExist,
    setIsAuthorEmailExist,
  };
};
